/**
 * Resolves SMS "starting at" price from a geo-based getAddOns response (lite/bronze-plan anchor).
 * Mirrors libs/domain-layer/.../get-sms-starting-price.ts without a domain-layer import.
 *
 * Lookup prefers `CTCT-SMS-ADDON`, then falls back to other `CTCT-SMS-ADDON*` response ids.
 * Accepts raw getAddOns JSON (`[{ id, priceTiers }]`) or AddOnsVO-shaped objects with `.getAddOns()`.
 */
import { getStartingPriceFromAddOn } from './getAddonStartingPriceFromAddOn';
import { findSmsAddOnInList } from './smsAddOnUtils';
import AddOnIds from '../constants/addOnIds';

interface RawAddOn {
  id?: string;
  priceTiers?: { tierMin?: number; price: number | { priceAmount: number } }[];
}

interface AddOnVO {
  getId: () => string;
  priceTiers?: RawAddOn['priceTiers'];
}

interface AddOnsVO {
  getAddOns: () => AddOnVO[];
}

const hasPriceTiers = (addOn: RawAddOn | undefined): boolean => Boolean(addOn?.priceTiers?.length);

const normalizeAddOn = (addOn: RawAddOn | AddOnVO): RawAddOn => {
  if (typeof (addOn as AddOnVO).getId === 'function') {
    const vo = addOn as AddOnVO;

    return {
      id: vo.getId(),
      priceTiers: vo.priceTiers ?? [],
    };
  }

  return addOn as RawAddOn;
};

const normalizeAddOnsList = (addOns: RawAddOn[] | AddOnsVO | null | undefined): RawAddOn[] | null => {
  if (!addOns) {
    return null;
  }

  if (Array.isArray(addOns)) {
    return addOns.map(normalizeAddOn);
  }

  if (typeof (addOns as AddOnsVO).getAddOns === 'function') {
    return (addOns as AddOnsVO).getAddOns().map(normalizeAddOn);
  }

  return null;
};

export function getSmsStartingPriceFromAddOns(addOns: RawAddOn[] | AddOnsVO | null | undefined): number | null {
  const addOnList = normalizeAddOnsList(addOns);

  if (!addOnList?.length) {
    return null;
  }

  const smsAddOn = findSmsAddOnInList(addOnList, AddOnIds.CTCT_SMS_ADDON, hasPriceTiers);

  if (!smsAddOn) {
    return null;
  }

  const startingPrice = getStartingPriceFromAddOn(smsAddOn);

  return startingPrice > 0 ? startingPrice : null;
}
